/*
 * This file is part of glowingblue/orell-facade.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import PaginatedListState from 'flarum/common/states/PaginatedListState';
import app from 'flarum/forum/app';
import setRouteWithForcedRefresh from 'flarum/common/utils/setRouteWithForcedRefresh';
import ItemList from 'flarum/common/utils/ItemList';

export default class BannerListState extends PaginatedListState {
	get type() {
		return 'banners';
	}

	requestParams() {
		const params = { ...super.requestParams() }; // Clone for immutability

		params.sort = this.sortMap()[m.route.param('sort')];

		const filter = m.route.param('filter');
		if (filter) {
			if (filter.active) {
				const value = this.activeFilterMap()[filter.active];
				params.filter = {
					...params.filter,
					active: value,
				};
			}
			if (filter['-active']) {
				const value = this.activeFilterMap()[filter['-active']];
				params.filter = {
					...params.filter,
					'-active': value,
				};
			}
			if (filter.position) {
				const value = this.positionFilterMap()[filter.position];
				params.filter = {
					...params.filter,
					position: value,
				};
			}
		}

		return params;
	}

	/**
	 * Get a map of sort keys (which appear in the URL, and are used for
	 * translation) to the API sort value that they represent.
	 */
	sortMap() {
		const map = {};

		Object.keys(this.sortMapItems().toObject()).forEach((key) => {
			const item = this.sortMapItems().get(key);
			map[key] = item;
		});

		return map;
	}

	sortMapItems() {
		const items = new ItemList();

		items.add('newest', '-createdAt', 101);
		items.add('oldest', 'createdAt', 100);
		items.add('latest', 'updatedAt', 80);
		items.add('startDate', '-startDate', 71);
		items.add('endDate', '-endDate', 70);

		return items;
	}

	changeSort(sort) {
		const params = this.params;

		// Delete the implicit page and showAll parameters
		delete params.page;

		if (sort === Object.keys(this.sortMap())[0]) {
			delete params.sort;
		} else {
			params.sort = sort;
		}

		setRouteWithForcedRefresh(app.route(app.current.get('routeName'), params));
	}

	activeFilterMap() {
		return {
			any: 'any',
			active: 'active',
			inactive: '-active',
		};
	}

	positionFilterMap() {
		return {
			any: 'any',
			slider: 'slider',
			firstBlock: 'first_block',
			secondBlock: 'second_block',
			thirdBlock: 'third_block',
			fourthBlock: 'fourth_block',
			fifthBlock: 'fifth_block',
			sixthBlock: 'sixth_block',
			readingRound: 'reading_round',
			sponsorAboveReviews: 'sponsor_above_reviews',
			sponsorBelowReviews: 'sponsor_below_reviews',
			sponsorBetweenCommunityEvents: 'sponsor_between_community_events',
		};
	}

	changeFilter(filterKey, value) {
		const params = this.params;

		// Delete the implicit page and showAll parameters
		delete params.page;

		const filter = { ...(params.filter ?? {}) };

		// Reset
		delete filter[filterKey];
		delete filter[`-${filterKey}`];

		if (value !== Object.keys(this[filterKey + 'FilterMap']())[0]) {
			let filterKeyValue = filterKey;
			if (filterKey === 'active') {
				filterKeyValue = this[filterKey + 'FilterMap']()[value];
			}
			if (filterKey === 'active') {
				value = true;
			}
			filter[filterKeyValue] = value;
		}

		if (Object.keys(filter).length === 0) {
			delete params.filter;
		} else {
			params.filter = filter;
		}

		setRouteWithForcedRefresh(app.route(app.current.get('routeName'), params));
	}
}
